import { Image, Upload, Trash2, CheckCircle, AlertCircle, Map, Globe } from 'lucide-react';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { compressLogo } from '../lib/imageCompression';
import { useEffect, useState, useRef } from 'react';

export function AppearanceSettings() {
  const { globalSettings, updateGlobalSettings, currentUser } = useStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const emailVal = data.user?.email || data.user?.user_metadata?.email || localStorage.getItem('wfm_user_email');
      setUserEmail(emailVal || null);
    });
  }, []);

  const isSuperAdmin = userEmail?.toLowerCase().trim() === 'benmloukaamen@gmail.com' || 
    currentUser?.email === 'benmloukaamen@gmail.com';

  const MAX_SIZE_MB = 2;

  const processFile = async (file: File) => {
    setError('');
    setSuccess(false);

    if (!file.type.startsWith('image/')) {
      setError('Le fichier doit être une image (PNG, JPG, SVG, WEBP).');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Le fichier est trop lourd. Taille maximale : ${MAX_SIZE_MB} Mo.`);
      return;
    }

    try {
      const compressed = await compressLogo(file);
      updateGlobalSettings({ appLogoUrl: compressed });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Erreur lors du traitement du logo.');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const removeLogo = () => {
    updateGlobalSettings({ appLogoUrl: undefined });
    setSuccess(false);
    setError('');
  };

  return (
    <div className="h-full overflow-y-auto custom-scrollbar p-6 md:p-8">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <Image className="w-5 h-5 text-indigo-400" />
            </div>
            Apparence de l'application
          </h2>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">
            Personnalisez le logo affiché dans la barre de navigation (admin / manager) et dans l'en-tête de l'application team.
          </p>
        </div>

        {/* Current Logo Preview */}
        <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-widest">Logo actuel</h3>

          <div className="flex items-center gap-6">
            {/* Admin sidebar preview */}
            <div className="flex flex-col items-center gap-2">
              <div className="w-48 h-16 bg-[#0A0B0E] border border-[#2D3139] rounded-xl flex items-center justify-center px-4 relative overflow-hidden">
                <div className="absolute inset-0 opacity-30"
                  style={{ backgroundImage: 'repeating-linear-gradient(45deg, #1a1d23 0, #1a1d23 1px, transparent 0, transparent 50%)', backgroundSize: '8px 8px' }}
                />
                {globalSettings.appLogoUrl ? (
                  <img
                    src={globalSettings.appLogoUrl}
                    alt="Logo app"
                    className="max-h-10 max-w-full object-contain relative z-10"
                  />
                ) : (
                  <span className="text-xl font-bold text-emerald-400 relative z-10">FestPlanner</span>
                )}
              </div>
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Sidebar admin</span>
            </div>

            {/* Team header preview */}
            <div className="flex flex-col items-center gap-2">
              <div className="w-48 h-16 bg-[#14161B] border border-[#2D3139] rounded-xl flex items-center px-4 gap-3 relative overflow-hidden">
                <div className="absolute inset-0 opacity-30"
                  style={{ backgroundImage: 'repeating-linear-gradient(45deg, #1a1d23 0, #1a1d23 1px, transparent 0, transparent 50%)', backgroundSize: '8px 8px' }}
                />
                {globalSettings.appLogoUrl ? (
                  <img
                    src={globalSettings.appLogoUrl}
                    alt="Logo app"
                    className="max-h-8 max-w-[100px] object-contain relative z-10"
                  />
                ) : (
                  <span className="text-sm font-bold text-emerald-400 relative z-10">FestPlanner</span>
                )}
                <div className="w-6 h-6 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex-shrink-0 ml-auto relative z-10" />
              </div>
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Header team</span>
            </div>
          </div>
        </div>

        {/* Upload Zone */}
        <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 space-y-5">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-widest">Changer le logo</h3>

          <div
            className={`relative border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 cursor-pointer group ${
              dragOver
                ? 'border-indigo-500 bg-indigo-500/10'
                : 'border-[#2D3139] hover:border-indigo-500/50 hover:bg-[#1A1D23]'
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="flex flex-col items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-200 ${
                dragOver ? 'bg-indigo-500/20 scale-110' : 'bg-[#1A1D23] group-hover:bg-indigo-500/10'
              }`}>
                <Upload className={`w-7 h-7 transition-colors ${dragOver ? 'text-indigo-400' : 'text-slate-500 group-hover:text-indigo-400'}`} />
              </div>
              <div>
                <p className="text-white font-semibold mb-1">
                  Glissez-déposez votre logo ici
                </p>
                <p className="text-slate-400 text-sm">
                  ou <span className="text-indigo-400 underline underline-offset-2">parcourez vos fichiers</span>
                </p>
              </div>
              <p className="text-xs text-slate-500">PNG, JPG, SVG, WEBP · Max {MAX_SIZE_MB} Mo</p>
            </div>
          </div>

          {/* Feedback */}
          {error && (
            <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-4 rounded-xl">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm p-4 rounded-xl animate-in fade-in duration-300">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              Logo mis à jour avec succès !
            </div>
          )}

          {/* Remove button */}
          {globalSettings.appLogoUrl && (
            <button
              onClick={removeLogo}
              className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 px-4 py-2.5 rounded-xl transition-all font-medium"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer le logo (restaurer le nom par défaut)
            </button>
          )}
        </div>
        
        {/* Map Provider Selection (Superadmin only) */}
        {isSuperAdmin && (
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-widest flex items-center gap-2">
                <Map className="w-4 h-4 text-indigo-400" />
                Prestataire de Cartographie
              </h3>
              <p className="text-slate-500 text-xs mt-1">
                Choisissez le moteur de carte par défaut pour l'administration et l'application team.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Mapbox */}
              <button
                onClick={() => updateGlobalSettings({ mapProvider: 'mapbox' })}
                className={`relative p-5 rounded-2xl border-2 text-left transition-all group overflow-hidden ${
                  globalSettings.mapProvider === 'mapbox' || !globalSettings.mapProvider
                    ? 'border-indigo-500 bg-indigo-500/10 shadow-[0_0_20px_rgba(99,102,241,0.15)]'
                    : 'border-[#2D3139] hover:border-[#3D4149] bg-[#0A0B0E]'
                }`}
              >
                <div className="flex items-center gap-4 relative z-10">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    globalSettings.mapProvider === 'mapbox' || !globalSettings.mapProvider
                      ? 'bg-indigo-500 text-white'
                      : 'bg-[#1A1D23] text-slate-500 group-hover:text-slate-400'
                  }`}>
                    <Map className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Mapbox</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black">Design 3D Immersif</p>
                  </div>
                </div>
                {globalSettings.mapProvider === 'mapbox' || !globalSettings.mapProvider ? (
                  <div className="absolute top-2 right-2">
                    <CheckCircle className="w-5 h-5 text-indigo-500" />
                  </div>
                ) : null}
              </button>

              {/* Google Maps */}
              <button
                onClick={() => updateGlobalSettings({ mapProvider: 'google' })}
                className={`relative p-5 rounded-2xl border-2 text-left transition-all group overflow-hidden ${
                  globalSettings.mapProvider === 'google'
                    ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.15)]'
                    : 'border-[#2D3139] hover:border-[#3D4149] bg-[#0A0B0E]'
                }`}
              >
                <div className="flex items-center gap-4 relative z-10">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    globalSettings.mapProvider === 'google'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-[#1A1D23] text-slate-500 group-hover:text-slate-400'
                  }`}>
                    <Globe className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Google Maps</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black text-amber-500/80">Secours / Fallback</p>
                  </div>
                </div>
                {globalSettings.mapProvider === 'google' ? (
                  <div className="absolute top-2 right-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                ) : null}
              </button>
            </div>
            
            <p className="text-[10px] text-slate-500 italic text-center">
              En cas de problème technique avec l'un des fournisseurs, basculez en un clic pour restaurer le service.
            </p>
          </div>
        )}

        {/* Info card */}
        <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-5 text-sm text-slate-400 leading-relaxed space-y-2">
          <p className="text-indigo-300 font-semibold flex items-center gap-2">
            <Image className="w-4 h-4" /> Conseils
          </p>
          <ul className="list-disc list-inside space-y-1 pl-1">
            <li>Utilisez un logo sur fond transparent (PNG ou SVG) pour un rendu optimal.</li>
            <li>Dimensions recommandées : hauteur max <strong className="text-slate-300">40–60 px</strong>.</li>
            <li>Le logo est stocké dans la base de données et visible par tous les utilisateurs connectés.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
